/**
 * VatRegisterViewElement — kreator formatek: klocek palety reprezentujący RĘCZNY
 * rejestr VAT faktury (`vat_register_view`, patrz `RecursiveFormRenderer`
 * PLACEHOLDER_TYPES).
 *
 * Mirror `DocumentItemsViewElement.tsx` (patrz nagłówek tamtego pliku) — na REALNEJ
 * stronie faktury ten typ jest dispatchowany WYŁĄCZNIE przez
 * `contextRenderers['vat_register_view']` (→ `DocumentVatRegisterEditor`/
 * `DocumentVatRegisterSection`, `apps/admin/src/components/documents/`) — ten
 * rejestr obsługuje TYLKO podgląd w canvasie buildera (mode='builder'), gdzie nie
 * ma prawdziwego dokumentu do wyświetlenia.
 *
 * Płótno buildera (`mode='builder'`) pokazuje edytor układu kolumn
 * (`VatRegisterColumnsCanvas` → `TableLayoutEditor`), który zapisuje `settings.tableColumns` /
 * `settings.hiddenColumnIds`; widok odczytu i edytor wierszy na stronie faktury czytają te klucze
 * (prop `columnSettings` — jeden układ dla odczytu i edycji). Bez zapisu kolumny są stałe, w
 * kolejności WF.
 *
 * `allowedModules` — TYLKO 2 moduły faktur (rejestr VAT dotyczy wyłącznie faktur
 * zakupu/sprzedaży — pozostałe 5 klas dokumentu nie ma ewidencji VAT).
 *
 * `render` jest wołany jak ZWYKŁA FUNKCJA (nie komponent) — patrz
 * `LayoutElementRegistry.render` — więc żaden hook nie może być wywołany
 * BEZPOŚREDNIO w jej ciele: hooki żyją w komponencie-dziecku `VatRegisterColumnsCanvas`,
 * a placeholder używa `i18n.t` standalone zamiast `useTranslation`.
 */

import { Percent } from 'lucide-react';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import i18n from '@/i18n';

import { VatRegisterColumnsCanvas } from './VatRegisterColumnsCanvas';

export const VatRegisterViewElement: LayoutElementDefinition = {
  type: 'vat_register_view',
  category: 'tresc',
  isContainer: false,
  label: 'Rejestr VAT',
  labelKey: 'tenant.documents.vatRegister.paletteLabel',
  icon: 'Percent',
  allowedModules: ['purchase_invoices', 'sale_invoices'],
  // Jeden na layout — nakładka per-tenant (ukryte kolumny) jest kluczowana TYPEM elementu.
  layoutSingleton: true,
  // Edytor kolumn na płótnie jest szeroką tabelą — cały wiersz gridu sekcji, nie pół komórki.
  fullWidth: true,
  defaultSettings: () => ({}),
  // Płótno kreatora = edytor układu kolumn; poza kreatorem zostaje placeholder — realną stronę
  // faktury obsługuje `contextRenderers`.
  render: ({ element, mode, onSettingsChange, systemHideOnly }) =>
    mode === 'builder' ? (
      <VatRegisterColumnsCanvas
        element={element}
        onSettingsChange={onSettingsChange}
        systemHideOnly={systemHideOnly === true}
      />
    ) : (
      <div
        className="rounded-lg border border-dashed border-border-secondary bg-bg-secondary p-4 text-center"
        data-testid={`vat-register-view-element-${element.id}`}
        data-element-type="vat_register_view"
      >
        <Percent className="mx-auto h-5 w-5 text-text-muted" aria-hidden="true" />
        <p className="mt-1 text-xs font-medium text-text-secondary">
          {i18n.t('tenant.documents.vatRegister.paletteLabel', {
            defaultValue: 'Rejestr VAT',
          })}
        </p>
      </div>
    ),
};
